import { of } from 'rxjs/observable/of';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Project, PaginatedProjectDTO, ProjectUserInput } from '../models/project-model';
import { Bom } from '../models/bom';
import { Worksheet } from '../models/worksheet';
import { ProjectHealth, ProjectHealthInput } from '../models/project-health';
import { BomLine,BomLineInput, BomLinesRequest, WorksheetCommentsRequest } from '../models/bom-line';
import { forkJoin } from 'rxjs/observable/forkJoin';
import 'rxjs/add/operator/catch';
import { environment } from '../../environments/environment';
import { TransactionSummary } from '../models/transaction-summary';
import { Quote } from '../models/quote';
import { QuoteTransaction } from '../models/quote-transaction';
import { Order } from '../models/order';
import { OrderTransaction } from '../models/order-transaction';
import { CableCut } from '../models/cable-cut';
import { ProjectSummary, ProjectOrderInputs, ProjectOrderInput } from '../models/project-summary';
import { OrderConfirmation } from '../models/order-confirmation';
import { PaginatedRequest } from '../models/paginated-request';
import { MarketOrderInput, MarketOrderSavedFilter, MarketOrderSummary } from '../models/market-order-summary';
import { WorksheetActivityLog } from '../models/activity-log';
import { ApprovalReasonInput } from '../models/approval';
import { OrderDetail } from '../models/order-detail';
import { QuoteRequest } from '../models/quote-request';
import { QuoteAdvisoriesResult } from '../models/quote-advisories';
import { BehaviorSubject } from 'rxjs';
import { BulkProjectOutput } from '../models/bulk-project-output';

@Injectable()
export class ProjectService {

  baseUrl = environment.projectServiceBaseUrl;
  tesscoProxyBaseUrl = environment.tesscoProxyServiceBaseUrl;

  public refreshWorksheet = new BehaviorSubject(false);
  addRowsToWorksheetClicked = new BehaviorSubject(false);
  
  constructor(private http: HttpClient) {
  }

  getOrderSimulation(worksheetId: string, shipViaOption?: string, forceSimulation?: boolean) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    if (shipViaOption === undefined) {
      shipViaOption = null;
    }
    if (forceSimulation === undefined) {
      forceSimulation = false;
    }
    const url = `${this.baseUrl}v2/order/simulate`;
    return this.http.post<OrderDetail>(url, {worksheetId: worksheetId, shipViaOption: shipViaOption,
      forceSimulation: forceSimulation}, { headers });
  }

  getOrderSimulation2(quoteRequest : QuoteRequest) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}v2/order/simulate`;
    return this.http.post<OrderDetail>(url, quoteRequest, { headers });
  }

  getMyProjects(marketId: string, projectUserInput: ProjectUserInput, paginatedRequest: PaginatedRequest) {
    const url = `${this.baseUrl}project/market/${marketId}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const params = new HttpParams()
      .set('page', paginatedRequest.page)
      .set('size', paginatedRequest.pageSize)
      .set('sort', paginatedRequest.sorting);
    return this.http.post<PaginatedProjectDTO>(url, projectUserInput, { params, headers });
  }

  //Not in use
  getOrdersLines(projectId: string) {
    const url = `${this.baseUrl}project/${projectId}/bomLinesByProjectId`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get<any[]>(url, { headers });
  }

  getMyOrders(projectId: string, userId: string) {
    const url = `${this.baseUrl}v2/project/${projectId}/ordersByUserId`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.post<Worksheet[]>(url, { userId: userId }, { headers });
  }

  createProject(project: Project) {
    const url = `${this.baseUrl}project`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.post<Project>(url, project, { headers });
  }

  getProjectById(projectId: string, projectPermission: boolean, saveViewHistory: boolean) {
    const url = `${this.baseUrl}project/${projectId}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    var projectPerm = projectPermission + "";
    const params = new HttpParams()
      .set("projectPerm", projectPerm)
      .set("saveViewHistory", saveViewHistory + "");
    return this.http.get<Project>(url, { params, headers });
  }

  getProjectByNameAndAccountNumber(projectName: string, accountNumber: string,) {
    const url = `${this.baseUrl}project/byNameAndAccountNumber/${encodeURIComponent(projectName)}/${accountNumber}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get<Project>(url, { headers });
  }

  getAllMasterBom() {
    const url = `${this.baseUrl}bom/allMaster`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get<Bom[]>(url, { headers });
  }

  getAllMasterBomByMarket(marketId: string) {
    const url = `${this.baseUrl}bom/allMasterBomByMarket/${marketId}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get<Bom[]>(url, { headers });
  }

  createBom(name: string, type: string = 'MasterBom') {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}bom`;
    return this.http.post<Bom>(url, { name, type }, { headers });
  }

  getBom(bomId: string) {
    const url = `${this.baseUrl}bom/${bomId}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get<Bom>(url, { headers });
  }

  deleteBom(id: string, marketIds: string[]) {
    const url = `${this.baseUrl}bom/${id}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
      const body = { markets: marketIds };
      return this.http.request('delete', url, { body: body, headers });
  }
//not in use
  addMarketsToBom(id: string, marketIds: string[]) {
    const url = `${this.baseUrl}bom/${id}/markets`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const body = { markets: marketIds };
    return this.http.post<void>(url, body, { headers });
  }

  removeMarketsFromBom(id: string, marketIds: string[]) {
    const url = `${this.baseUrl}bom/${id}/markets`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const body = { markets: marketIds };
    return this.http.request('delete', url, { body: body, headers });
  }

  GetCableCuts(bomLineId: string, bomLineItemId: string) {
    return this.getBomLines(bomLineId).map(bomLines => {
      return bomLines.find(bl => bl.id === bomLineItemId).cableCuts;
    });
  }

  createCableCut(bomLineId: string, cableCut: CableCut) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}bom/lines/cablecuts`;
    const newCableCut = Object.assign({}, { bomLineId },
      { length: cableCut.length }, { quantity: cableCut.quantity }, { confirmed: cableCut.confirmed });
    return this.http.post<void>(url, newCableCut, { headers });
  }

  createCableCuts(bomLineId: string, cableCuts: CableCut[]) {
    return forkJoin(cableCuts.map(cableCut => this.createCableCut(bomLineId, cableCut)));
  }

  deleteCableCut(cableCutId: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}bom/lines/cablecuts/${cableCutId}`;
    return this.http.delete<void>(url, { headers });
  }

  deleteCableCuts(cableCutIds: string[]) {
    return forkJoin(cableCutIds.map(cableCutId => this.deleteCableCut(cableCutId)));
  }

  updateCableCuts(cableCuts: CableCut[]) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}bom/lines/cablecuts`;
    return this.http.put<BomLine>(url, cableCuts, { headers });
  }

  quickAddBomLines(bomlineInputList: BomLineInput[]) {
    const headers = new HttpHeaders()
    .set('Accept', 'application/json');
    const url = `${this.baseUrl}bom/lines/quickAdd`;
    return this.http.post<BomLine[]>(url, bomlineInputList, { headers });
  }

  // createBomLineV2(input: BomLineInput) {
  //   const headers = new HttpHeaders()
  //   .set('Accept', 'application/json');
  //   const url = `${this.baseUrl}v2/bom/lines`;
  //   return this.http.post<BomLine>(url, input, { headers });
  // }

  createBomLinesV2(input: BomLinesRequest) {
    const headers = new HttpHeaders()
    .set('Accept', 'application/json');
    const url = `${this.baseUrl}v2/bom/lines/all`;
    return this.http.post<BomLine[]>(url, input, { headers });
  }

  deleteBomLine(lineId: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}bom/lines/${lineId}`;
    return this.http.delete<void>(url, { headers });
  }

  updateBomLine(bomId: string, bomLine: BomLine) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}bom/lines/${bomLine.id}`;
    const editable = Object.assign({}, bomLine, { bomId });
    return this.http.put<BomLine[]>(url, editable, { headers })
      // .map(bomLineArray => bomLineArray[0]);
      ;
  }

  deleteBomLines(lineIds: string[]) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}bom/deleteLines/`;
    return this.http.post<any>(url, { bomlineIds: lineIds }, { headers })
  }

  getBomLinesByBomlineID(bomLineId: string){
    const headers = new HttpHeaders().set('Accept', 'application/json');
    const url = `${this.baseUrl}bomline/lineid/${bomLineId}`;
    return this.http.get<BomLine>(url, { headers })
  }

  getBomLines(bomId: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}bom/lines/${bomId}`;
    return this.http.get<BomLine[]>(url, { headers })
      .catch((error: any) => {
        if (error.status === 404) {
          return of([] as BomLine[]);
        } else {
          throw error;
        }
      });
  }

  getBomLinesWithConsignment(bomId: string, accountNumber: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}bom/lines/${bomId}/${accountNumber}`;
    return this.http.get<BomLine[]>(url, { headers })
      .catch((error: any) => {
        if (error.status === 404) {
          return of([] as BomLine[]);
        } else {
          throw error;
        }
      });
  }

  updateProject(project: Project) {
    const url = `${this.baseUrl}project/${project.id}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.put<Project>(url, project, { headers });
  }

  deleteProject(id: string) {
    const url = `${this.baseUrl}project/${id}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.delete<void>(url, { headers });
  }

  activeProject(id: string) {
    const url = `${this.baseUrl}project/active/${id}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.put<void>(url, { headers });
  }

  recAProject(id: string) {
    const url = `${this.baseUrl}project/reca/${id}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.put<void>(url, { headers });
  }

  bulkUpdateRecA(projectUserInput: ProjectUserInput) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}project/bulkReca`;
    return this.http.put<void>(url, projectUserInput, { headers });
  }

  sendWorksheetBomChangesNotification(id: string) {
    const url = `${this.baseUrl}worksheet/${id}/changes/notification`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.post<void>(url, { headers });
  }

  bulkNonRecAUpdateProjectStatus(projectUserInput: ProjectUserInput) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}project/bulkNonReca`;
    return this.http.put<void>(url, projectUserInput, { headers });
  }

  bulkUpdateProjectStatus(projectUserInput: ProjectUserInput) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}project/bulk`;
    return this.http.put<void>(url, projectUserInput, { headers });
  }

  getOrderIdsForProjectId(projectId: string) {
    const url = `${this.baseUrl}project/${projectId}/orders`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get<string[]>(url, { headers });
  }

  getProjectTransactionSummary(projectId: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}project/${projectId}/transactionSummary`;
    return this.http.get<TransactionSummary>(url, { headers });
  }

  createWorksheet(worksheet: Worksheet) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}worksheet`;
    return this.http.post<Worksheet>(url, worksheet, { headers });
  }

  getProjectHealth(id: string, projectListFlag?: boolean) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    var projectListFlagString;
    if (projectListFlag) {
      projectListFlagString = projectListFlag + "";
    } else {
      projectListFlagString = 'false';
    }
    const params = new HttpParams()
      .set("projectListFlag", projectListFlagString);
    const url = `${this.baseUrl}v2/project/${id}/health`;
    return this.http.get<ProjectHealth>(url, { params, headers });
  }

  getProjectHealthByMarket(marketId: string, projectHealthInput: ProjectHealthInput[]) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}v2/project/health/${marketId}`;
    return this.http.post<ProjectHealth[]>(url, projectHealthInput, { headers } );
  }

  getOrder(worksheetId: string) {
    const url = `${this.baseUrl}worksheet/${worksheetId}/order/transactions`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get<OrderTransaction>(url, { headers });
  }

  getWorksheet(id: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}worksheet/${id}`;
    return this.http.get<Worksheet>(url, { headers });
  }

  updateWorksheet(id: string, worksheet: Worksheet) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}worksheet/${id}`;
    return this.http.put<Worksheet>(url, worksheet, { headers });
  }

  updateWorksheetWatchers(id: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}worksheet/${id}/watching`;
    return this.http.post<void>(url, { headers });
  }

  // getWorksheetsByProjectId(projectId: string) {
  //   const headers = new HttpHeaders()
  //     .set('Accept', 'application/json');
  //   const url = `${this.baseUrl}worksheet`;
  //   const params = new HttpParams()
  //     .set('projectId', projectId);
  //   return this.http.get<Worksheet[]>(url, { params, headers });
  // }

  getWorksheetsByProjectIdV2(projectId: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}v2/worksheets/projects/${projectId}`;
    return this.http.get<Worksheet[]>(url, { headers });
  }

  getWorksheetsByMarketId(marketId: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}worksheet`;
    const params = new HttpParams()
      .set('marketId', marketId);
    return this.http.get<Worksheet[]>(url, { params, headers });
  }

  deleteWorksheet(worksheetId: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
      const url = `${this.baseUrl}v2/worksheets/${worksheetId}`;
    return this.http.delete<void>(url, { headers });
  }

  deleteWorksheets(worksheetIds: string[], marketId: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}worksheets`;
    const params = new HttpParams()
      .set("worksheetIds", worksheetIds ? worksheetIds.join(",") : null)
      .set("marketId", marketId);
    return this.http.delete<void>(url, { params, headers });
  }

  getWorksheetPdf(worksheetId: string, salesTaxNeedsToBeDisplayed: boolean) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}v2/worksheets/${worksheetId}/pdf`;
    const params = new HttpParams()
      .set("salesTaxNeedsToBeDisplayed", String(salesTaxNeedsToBeDisplayed));
      return this.http.get(url, { params,headers, responseType: 'text' });
    }

  getWorksheetXls(worksheetId: string, userId: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/xlsx; charset=utf-8');
    const url = `${this.baseUrl}v2/worksheets/${worksheetId}/xls`;
    const params = new HttpParams()
      .set("userId", userId);
    return this.http.get(url, { params, headers, observe: 'response', responseType: 'blob' });
  }

  getWorksheetCSV(worksheetId: string, userId: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/csv; charset=utf-8');
    const url = `${this.baseUrl}v2/worksheets/${worksheetId}/csv`;
    const params = new HttpParams()
      .set("userId", userId);
    return this.http.get(url, { params, headers, observe: 'response', responseType: 'blob' });
  }

  exportWorksheetByEmail(worksheetId: string, format: string, userIdList: string[], userEmailList: string[]) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}v2/worksheets/${worksheetId}/export`;
    return this.http.post<void>(url, { format: format, userIdList: userIdList, userEmailList: userEmailList }, { headers });
  }

  // submitForApproval(id: string) {
  //   const headers = new HttpHeaders()
  //     .set('Accept', 'application/json');
  //   const url = `${this.baseUrl}worksheet/${id}/approval`;
  //   return this.http.post<string>(url, { headers });
  // }

  submitForApprovalV2(id: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}v2/worksheet/${id}/approval`;
    return this.http.post<Worksheet>(url, { headers });
  }

  getQuoteTransactions(worksheetId: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}worksheet/${worksheetId}/quote/transactions`;
    return this.http.get<Quote[]>(url, { headers });
  }

  getQuote(quoteTransactionId: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}worksheet/quote/transactions/${quoteTransactionId}`;
    return this.http.get<QuoteTransaction>(url, { headers });
  }

  getQuoteV2(worksheetId: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}v2/order/${worksheetId}/quote`;
    return this.http.get<OrderDetail>(url, { headers });
  }

  submitQuote(worksheetId: string, quote: Quote) {
    const url = `${this.baseUrl}worksheet/${worksheetId}/quote`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.post<Quote>(url, quote, { headers });
  }

  submitQuoteV2(quoteRequest: QuoteRequest) {
    const url = `${this.baseUrl}v2/order/quote`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.post<OrderDetail>(url, quoteRequest, { headers });
  }

  submitOrder(worksheetId: string) {
    const url = `${this.baseUrl}/worksheet/${worksheetId}/order`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.post<Order>(url, { headers });
  }

  submitPayment(poNumber: string, worksheetId: string) {
    const url = `${this.baseUrl}/worksheet/${worksheetId}/payment`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.post<any>(url, { poNumber: poNumber, type: 'PO', transactionNumber: poNumber }, { headers });
  }

  addShipmentNotificationUser(userId: string, worksheetId: string) {
    const url = `${this.baseUrl}/worksheet/${worksheetId}/addShipmentNotificationUser/${userId}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.post<any>(url, { headers });
  }

  /**
    * TESSCO PROXY METHODS
    */

  // getProjectOrderSummary(projectOrderInputs: ProjectOrderInputs) {
  //   const headers = new HttpHeaders()
  //     .set('Accept', 'application/json');
  //   const url = `${this.baseUrl}v2/project/order/summary`;
  //   return this.http.post<ProjectSummary>(url, projectOrderInputs, { headers });
  // }

  getProjectOrderSummaryV2(projectOrderInput: ProjectOrderInput) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}v2/project/order/summary2`;
    return this.http.post<ProjectSummary>(url, projectOrderInput, { headers });
  }

  getMarketOrderSummary(marketOrderInput: MarketOrderInput, paginatedRequest: PaginatedRequest) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}v2/market/order/summary`;
    const params = new HttpParams()
      .set('page', paginatedRequest.page)
      .set('size', paginatedRequest.pageSize)
      .set('sort', paginatedRequest.sorting);
    return this.http.post<MarketOrderSummary>(url, marketOrderInput, { params, headers });
  }

  // getOrderConfirmation(projectOrderInputs: ProjectOrderInputs) {
  //   const headers = new HttpHeaders()
  //     .set('Accept', 'application/json');
  //   const url = `${this.baseUrl}v2/project/order/confirmation`;
  //   return this.http.post<OrderConfirmation>(url, projectOrderInputs, { headers });
  // }

  getOrderConfirmationV2(projectOrderInput: ProjectOrderInput) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}v2/project/order/confirmation2`;
    return this.http.post<OrderConfirmation>(url, projectOrderInput, { headers });
  }


  /**
   * IMPORT FILES
   */

  createBomByImportFile(fileId: string, accountNumber: string, selectedOrgId: string, marketsToBeAdded: any) {
    const url = `${this.baseUrl}bom/import`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.post<Bom>(url,
      { fileId: fileId, selectedOrgId ,marketsToBeAdded, type: 'MasterBom', accountNumber: accountNumber },
      { headers });
  }

  createWorksheetDataLinesByImportCsvFile(fileId: string, worksheetId: string) {
    const url = `${this.baseUrl}worksheet/${worksheetId}/import/${fileId}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.post<BomLine[]>(url, { headers });
  }

  createProjectByImportCsvFile(fileId: string, marketId: string) {
    const url = `${this.baseUrl}projectFile/import`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.post<BulkProjectOutput>(url, { fileId: fileId, marketId: marketId }, { headers });
  }

  updateDeliverByDate(quoteTransactionId: string, quote: Quote) {
    const url = `${this.baseUrl}worksheet/quote/transactions/${quoteTransactionId}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.put<Quote>(url, quote, { headers });
  }

  getWorksheets(marketId: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}worksheet/v2`;
    const params = new HttpParams()
      .set('marketId', marketId);
    return this.http.get<Worksheet[]>(url, { params, headers });
  }

  getWorksheetsByProject(projectId: string, isQuoted: boolean, paginatedRequest: PaginatedRequest) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}worksheet/project/${projectId}`;
    let params = new HttpParams()
      .set('isQuoted', `${isQuoted}`)
      .set('page', paginatedRequest.page)
      .set('size', paginatedRequest.pageSize);
    return this.http.get<Worksheet[]>(url, { params, headers });
  }

  getProjects(marketId: string, paginatedRequest: PaginatedRequest) {
    const url = `${this.baseUrl}project/market/v2/${marketId}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    let params = new HttpParams()
      .set('page', paginatedRequest.page)
      .set('size', paginatedRequest.pageSize)
      if(paginatedRequest.sorting) {
        params.set('sort', paginatedRequest.sorting);
    }
    return this.http.get<Project[]>(url, { params, headers })
      .catch((error: any) => {
        if (error.status === 404) {
          return of([]);
        } else {
          throw error;
        }
      });
  }

  resubmitForApproval(id: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}worksheet/${id}/reapproval`;
    return this.http.post<Worksheet>(url, { headers });
  }

  savePurchasePlan(worksheetId: string, purchasePlanCode: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}worksheets/${worksheetId}/purchaseplan`;
    return this.http.put<Worksheet>(url, purchasePlanCode, { headers });
  }

  updateBOMMarkets(name: string, type: string, marketsToBeAdded: any, selectedOrgId: string, bomId: string, notes: string) {
    const headers = new HttpHeaders().set('Accept', 'application/json');
    const url = `${this.baseUrl}updatebom`;
    return this.http.post < Bom > (url, {name, type, marketsToBeAdded, selectedOrgId, bomId, notes},
      { headers });
}

  updateBOMAndAddOrRemoveFromMarket(name: string, marketsToBeAdded: string, marketsToBeRemoved: any,
      bomId: string, type: string = 'MasterBom') {
    const headers = new HttpHeaders()
        .set('Accept', 'application/json');
    const url = `${this.baseUrl}updatebom`;
        return this.http.post < Bom > (url,
          {name, type,marketsToBeAdded, marketsToBeRemoved, bomId },
          { headers}
        );
  }

  getProjectNameForWorksheet(id: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'text/plain');
    const url = `${this.baseUrl}worksheet/${id}/project/name`;
    return this.http.get(url, { headers, responseType: 'text' });
  }

  getProjectByWorksheetId(id: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}worksheet/${id}/project`;
    return this.http.get<Project>(url, { headers });
  }

  submitEQuote(id: string, value: string, tsrn: string){
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}v2/worksheets/equote/${id}?status=${value}&revision=${tsrn}`;
    return this.http.post<string>(url, { headers });
  }

  findTsrnValue(id: string){
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}equote/${id}`;
    return this.http.get(url, { headers });
  }

  // submitForTesscoReview(id: string, quote: Quote) {
  //   const headers = new HttpHeaders()
  //     .set('Accept', 'application/json');
  //   const url = `${this.baseUrl}worksheet/submitForTesscoReview/${id}`;
  //   return this.http.post<Worksheet>(url, quote, { headers });
  // }

  submitForTesscoReviewV2(id: string, quoteRequest: QuoteRequest) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}v2/worksheets/${id}/tesscoReview`;
    return this.http.post<Worksheet>(url, quoteRequest, { headers });
  }

  denyTesscoReview(id: string, approvalReason: ApprovalReasonInput) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}worksheet/denyTesscoReview/${id}`;
    return this.http.post<Worksheet>(url, approvalReason, { headers });
  }

  getWorksheetActivityLog(worksheetId: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}activity/log/${worksheetId}`;
    return this.http.get<WorksheetActivityLog[]>(url, { headers });
  }

  downloadProjectOrderSummary(projectOrderInput: ProjectOrderInput) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/xlsx; charset=utf-8');
    const url = `${this.baseUrl}v2/project/order/summary/export`;
    return this.http.post(url, projectOrderInput, { headers, observe: 'response', responseType: 'blob' });
  }

  updatePoLineNumber(bomId: string, bomLine: BomLine) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}bom/lines/poLineNumber/${bomLine.id}`;
    const editable = Object.assign({}, bomLine, { bomId });
    return this.http.put<BomLine[]>(url, editable, { headers });
  }

  retractWorksheet(worksheetId: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}/worksheet/${worksheetId}/retract`;
    return this.http.get<void>(url, { headers });
  }

  canRetractWorksheet(worksheetId: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}/worksheet/${worksheetId}/canRetractWorksheet`;
    return this.http.get<boolean>(url, { headers });
  }

  downloadMarketOrderSummary(marketOrderInput: MarketOrderInput) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/xlsx; charset=utf-8');
    const url = `${this.baseUrl}v2/market/order/summary/export`;
    return this.http.post(url, marketOrderInput, {  headers, observe: 'response', responseType: 'blob' });
  }

  submitOrderV2(worksheetId: string, quoteRequest: QuoteRequest) {
    const url = `${this.baseUrl}/v2/order/${worksheetId}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.post<Order>(url, quoteRequest, { headers });
  }

  getWorksheetInitiatorsIdByMarket(marketId: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}worksheet/initiatorsByMarket`;
    const params = new HttpParams()
      .set('marketId', marketId);
    return this.http.get<any>(url, { params, headers });
  }

  getWorksheetApproversIdByMarket(marketId: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}worksheet/approversByMarket`;
    const params = new HttpParams()
      .set('marketId', marketId);
    return this.http.get<any>(url, { params, headers });
  }

  sendOrderAddressChangedNotification(worksheetId: string, addressId: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}worksheet/${worksheetId}/orderAddressChangedNotification/${addressId}`;
    return this.http.get<void>(url, { headers });
  }

  uploadBPAFile(fileId: string, atcType: string) {
    const url = `${this.baseUrl}bpa/upload/${fileId}/${atcType}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.post<void>(url, { headers });
  }

  getUserMosSavedFilters(marketId: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}v2/market/order/filter/list`;
    const params = new HttpParams()
      .set('marketId', marketId);
    return this.http.get<MarketOrderSavedFilter[]>(url, { params, headers });
  }

  saveNewMosFilter(mosSavedFilter: MarketOrderSavedFilter) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}v2/market/order/filter`;
    return this.http.post<MarketOrderSavedFilter>(url, mosSavedFilter, { headers });
  }

  updateMosFilter(mosSavedFilter: MarketOrderSavedFilter) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}v2/market/order/filter`;
    return this.http.put<MarketOrderSavedFilter>(url, mosSavedFilter, { headers });
  }

  saveBomLineAdvisories(advisoryList: QuoteAdvisoriesResult[]){
    const headers = new HttpHeaders()
    .set('Accept', 'application/json');
    const url = `${this.baseUrl}v2/bom/advisory`;
    return this.http.put<void>(url, advisoryList, { headers });
  }

  updateBomLinesIndex(bomlineInputList: BomLineInput[]) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}bom/lines/updateIndex`;
    return this.http.post<void>(url, bomlineInputList, { headers });
   }

   getActiveProjectCount(marketId: string) {
    const url = `${this.baseUrl}project/active/count/${encodeURIComponent(marketId)}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get<any>(url, { headers });
  }

  getBomLineAdvisoryList(bomLineIds: any) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const params = new HttpParams()
      .set("bomLineIds", bomLineIds);
    const url = `${this.baseUrl}v2/bom/getAdvisoryDetails`;
    return this.http.get<QuoteAdvisoriesResult[]>(url, { params, headers });
  }

  uploadBulkProjectsFile(fileId: string, marketId: string, atcType: string) {
    const url = `${this.baseUrl}project/bulkUpload/${fileId}/${marketId}/${atcType}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.post<void>(url, { headers });
  }

  updateBomLinesPOLineNumbers(bomlineInputList: BomLineInput[]) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}bom/lines/updatePOLineNumbers`;
    return this.http.post<void>(url, bomlineInputList, { headers });
   }

   uploadBulkProjectAsync(fileId: string, marketId: string) {
    const url = `${this.baseUrl}projectFile/import/async`;
    const headers = new HttpHeaders()
      .set('Accept', 'text/plain');
    return this.http.post(url, { fileId: fileId, marketId: marketId }, { headers, responseType: 'text' });
   }

   downloadWorksheetActivityLogs(worksheetId: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/xlsx; charset=utf-8');
    const url = `${this.baseUrl}activity/log/${worksheetId}/export`;
    return this.http.get(url, {  headers, observe: 'response', responseType: 'blob' });
  }

  downloadBPAData(atcType) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/xlsx; charset=utf-8');
    const url = `${this.baseUrl}bpa/download/${atcType}`;
    return this.http.get(url, { headers, observe: 'response', responseType: 'blob' });
  }

  getProjectsForMarket(marketId: string) {
    const url = `${this.baseUrl}project/market/list/${marketId}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get<Project[]>(url, { headers });
  }

    sendWSCommentNotification(worksheetCommentsRequest: WorksheetCommentsRequest) {
      const headers = new HttpHeaders()
        .set('Accept', 'application/json');
      const url = `${this.baseUrl}v2/worksheets/comment/notification`;
      return this.http.post<void>(url, worksheetCommentsRequest, { headers });
    }
}



// WEBPACK FOOTER //
// /codebuild/output/src3408204168/src/bitbucket.org/tesscotds/webclient/src/api/services/project.services.ts